import { useState, useCallback } from 'react';
import { useApiStore } from '../store/api';

interface ReactionUser {
  id: string;
  message_id: string;
  user_id: string;
  username: string;
  emoji: string;
  created_at: string;
}

export interface UseReactionUsersReturn {
  showWhoReacted: { messageId: string; emoji: string } | null;
  reactionUsers: ReactionUser[];
  loadingReactionUsers: boolean;
  loadWhoReacted: (conversationId: string, messageId: string, emoji: string) => Promise<void>;
  closeWhoReacted: () => void;
}

/**
 * Hook for loading and displaying which users reacted to a message.
 */
export function useReactionUsers(): UseReactionUsersReturn {
  const { apiUrl } = useApiStore();
  const [showWhoReacted, setShowWhoReacted] = useState<{
    messageId: string;
    emoji: string;
  } | null>(null);
  const [reactionUsers, setReactionUsers] = useState<ReactionUser[]>([]);
  const [loadingReactionUsers, setLoadingReactionUsers] = useState(false);

  const loadWhoReacted = useCallback(
    async (conversationId: string, messageId: string, emoji: string) => {
      try {
        setLoadingReactionUsers(true);
        const response = await fetch(
          `${apiUrl}/v1/conversations/${conversationId}/messages/${messageId}/reactions/${encodeURIComponent(emoji)}/users`,
          { credentials: 'include' }
        );
        if (response.ok) {
          const data = await response.json();
          setReactionUsers(data.users || []);
          setShowWhoReacted({ messageId, emoji });
        }
      } catch (err) {
        console.error('Failed to load reaction users:', err);
      } finally {
        setLoadingReactionUsers(false);
      }
    },
    [apiUrl]
  );

  const closeWhoReacted = useCallback(() => {
    setShowWhoReacted(null);
  }, []);

  return {
    showWhoReacted,
    reactionUsers,
    loadingReactionUsers,
    loadWhoReacted,
    closeWhoReacted,
  };
}
